import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Alert, Checkbox, Form, Modal, Radio, Space } from "antd";
import * as React from "react";
import { Button, DatePicker } from "src/components";
import { colors } from "src/constants";
import { WebsiteSystemItem, useUpdateWebsiteSystem } from "src/hooks/useWebsiteSystem";
import { WEBSITE_SYSTEM_PRODUCTS, WebsiteSystemProduct } from "src/services/websiteSystem";
import {
  DAY_ORDER,
  ScheduleFormValue,
  WebsiteSystemTypeTime,
  deserializeScheduleForm,
  serializeScheduleForm,
  validateScheduleForm,
} from "./scheduleForm";
import { selectedWebsiteProducts, WebsiteLogo } from "./WebsiteLogos";

// Shown Sunday-first like the old page; the values stay the backend's 0 = Monday
// index (see DAY_ORDER), so the Sunday button is "6".
const DAY_BUTTONS: { value: string; label: string }[] = [
  { value: "6", label: "อา" },
  { value: "0", label: "จ" },
  { value: "1", label: "อ" },
  { value: "2", label: "พ" },
  { value: "3", label: "พฤ" },
  { value: "4", label: "ศ" },
  { value: "5", label: "ส" },
];

type EditFormFields = {
  // Only a PER_PRODUCT row owns this field - an ALL-scope row shows the sites
  // locked, outside the form, so there is nothing to validate or send.
  sites?: WebsiteSystemProduct[];
};

type SitePickerProps = {
  value?: WebsiteSystemProduct[];
  onChange?: (value: WebsiteSystemProduct[]) => void;
  disabled?: boolean;
};

// ทุกเว็บ plus one box per site. It is a controlled child of Form.Item (value and
// onChange are injected), so a click on ทุกเว็บ takes the same path as a click on
// a site and the form re-validates - a stale "pick at least one site" message
// clears. Form.useWatch, which would let the parent read the value instead, only
// exists from antd 4.20.
const SitePicker: React.FC<SitePickerProps> = ({ value = [], onChange, disabled }) => {
  const selectedCount = WEBSITE_SYSTEM_PRODUCTS.filter((product) => value.includes(product)).length;
  const allSelected = selectedCount === WEBSITE_SYSTEM_PRODUCTS.length;
  return (
    <>
      <Checkbox
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked ? [...WEBSITE_SYSTEM_PRODUCTS] : [])}
        style={{ marginBottom: 8 }}
      >
        ทุกเว็บ
      </Checkbox>
      <Checkbox.Group
        value={value}
        disabled={disabled}
        onChange={(checked) => onChange?.(checked as WebsiteSystemProduct[])}
        style={{ display: "block" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 32,
            background: colors.gray1,
            border: `1px solid ${colors.gray5}`,
            borderRadius: 8,
            padding: "12px 16px",
          }}
        >
          {WEBSITE_SYSTEM_PRODUCTS.map((product) => (
            <Checkbox
              key={product}
              value={product}
              style={{ display: "flex", alignItems: "center", marginLeft: 0 }}
            >
              <WebsiteLogo product={product} />
            </Checkbox>
          ))}
        </div>
      </Checkbox.Group>
    </>
  );
};

// Light grey strip between the header and the two form sections. The modal body
// pads 24px on every side, so the strip pulls out by that much to run edge to
// edge; flushTop also cancels the top padding so it sits against the header.
const SectionBand: React.FC<{ flushTop?: boolean }> = ({ flushTop }) => (
  <div
    aria-hidden
    style={{ height: 8, margin: `${flushTop ? -24 : 0}px -24px 16px`, background: colors.gray4 }}
  />
);

export type WebsiteSystemEditModalProps = {
  item: WebsiteSystemItem;
};

type EditModalBodyProps = {
  item: WebsiteSystemItem;
  visible: boolean;
  hide: () => void;
  remove: () => void;
};

// All of this row's editing state lives here rather than directly in
// WebsiteSystemEditModal, and is mounted with key={item.code} there - NiceModal
// can reuse an already-mounted modal instance if show() is called again while it
// is still transitioning closed (e.g. cancel on GameError, immediately open
// KYCError). Without the key, useRef/useState/Form here would keep the previous
// row's values while item.code pointed at the new one, so a save would write the
// old row's data onto the new row.
const EditModalBody: React.FC<EditModalBodyProps> = ({ item, visible, hide, remove }) => {
  const [form] = Form.useForm<EditFormFields>();
  const updateMutation = useUpdateWebsiteSystem();

  const initialSchedule = React.useRef<ScheduleFormValue>(deserializeScheduleForm(item)).current;
  const [schedule, setSchedule] = React.useState<ScheduleFormValue>(initialSchedule);
  const [submitError, setSubmitError] = React.useState<string>();

  const setTypeTime = (typeTime: WebsiteSystemTypeTime) => setSchedule((prev) => ({ ...prev, typeTime }));
  const toggleDay = (day: string) =>
    setSchedule((prev) => ({
      ...prev,
      daysWeek: prev.daysWeek.includes(day) ? prev.daysWeek.filter((d) => d !== day) : [...prev.daysWeek, day],
    }));
  const toggleAllDays = () =>
    setSchedule((prev) => ({
      ...prev,
      // DAY_ORDER, not DAY_BUTTONS: serializeScheduleForm joins the days as-is, and
      // the buttons' on-screen order (Sunday first) must not leak into days_week.
      daysWeek: prev.daysWeek.length === 7 ? [] : [...DAY_ORDER],
    }));

  const customStartUnchanged =
    schedule.typeTime === "custom" &&
    initialSchedule.typeTime === "custom" &&
    !!schedule.customStart &&
    !!initialSchedule.customStart &&
    schedule.customStart.isSame(initialSchedule.customStart);

  // While the row is on, an ended custom window would have the backend switch
  // the row off in this very save - behind a "บันทึกสำเร็จ" - so it is refused here.
  const scheduleError = validateScheduleForm(schedule, { customStartUnchanged, requireFutureEnd: item.enabled });

  const submit = async () => {
    setSubmitError(undefined);
    if (scheduleError) {
      setSubmitError(scheduleError);
      return;
    }
    let values: EditFormFields;
    try {
      values = await form.validateFields();
    } catch {
      return; // antd already highlights the offending fields
    }

    const serializedSchedule = serializeScheduleForm(schedule, item.stored);
    try {
      await updateMutation.mutateAsync({
        code: item.code,
        scope: item.scope,
        products: item.scope === "PER_PRODUCT" ? values.sites : undefined,
        typeTime: serializedSchedule.type_time,
        daysWeek: serializedSchedule.days_week,
        startDatetime: serializedSchedule.start_datetime,
        endDatetime: serializedSchedule.end_datetime,
      });
      hide();
    } catch (error: any) {
      // useUpdateWebsiteSystem's mutationFn does not wrap its rejection (unlike
      // the GET/toggle/audit hooks), so this reads the AxiosError directly -
      // error.response.data.service_message on a 4xx/5xx.
      setSubmitError(error?.response?.data?.service_message || error?.message || "บันทึกไม่สำเร็จ");
    }
  };

  // Double-click guard: set before the first await (validateFields is async, so
  // the button's own loading state is not up yet when a second click lands) and
  // cleared however submit() ends.
  const submittingRef = React.useRef(false);
  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await submit();
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <Modal
      visible={visible}
      afterClose={remove}
      title={
        // Padded clear of the close button, which sits over the header's right edge.
        <div style={{ textAlign: "center", padding: "0 32px" }}>{`แก้ไขระบบเว็บไซต์ • ${item.name}`}</div>
      }
      onCancel={hide}
      // A click outside must not throw away what was typed; × and Esc still close.
      maskClosable={false}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
          <Button size="large" onClick={hide} style={{ width: 100 }}>
            ยกเลิก
          </Button>
          <Button
            size="large"
            type="primary"
            loading={updateMutation.isLoading}
            onClick={handleSubmit}
            style={{ width: 160 }}
          >
            บันทึก
          </Button>
        </div>
      }
    >
      <SectionBand flushTop />

      {submitError && <Alert type="error" message={submitError} showIcon style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          // The saved selection, not which sites are live right now - a row that
          // is switched off still opens with the sites it will come back on.
          sites: selectedWebsiteProducts(item.products),
        }}
      >
        <Form.Item label="ช่วงเวลาที่แสดง" required>
          <Radio.Group value={schedule.typeTime} onChange={(e) => setTypeTime(e.target.value)}>
            <Space direction="vertical" size={12}>
              <Radio value="now">ทันที</Radio>
              <div>
                <Radio value="schedule">กำหนดวันเวลา - แบบง่าย</Radio>
                {/* Only the selected radio's controls are mounted; what was entered
                    stays in `schedule`, so switching away and back loses nothing. */}
                {schedule.typeTime === "schedule" && (
                  <div style={{ marginTop: 12, paddingLeft: 24 }}>
                    <Space>
                      <Button
                        shape="round"
                        type={schedule.daysWeek.length === 7 ? "primary" : "default"}
                        onClick={toggleAllDays}
                      >
                        ทุกวัน
                      </Button>
                      {DAY_BUTTONS.map((day) => (
                        <Button
                          key={day.value}
                          shape="circle"
                          type={schedule.daysWeek.includes(day.value) ? "primary" : "default"}
                          onClick={() => toggleDay(day.value)}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </Space>
                    <Form.Item label="เวลาเริ่มต้น" style={{ marginTop: 12, marginBottom: 0 }}>
                      {/* order={false}: without it the picker swaps start and end when the
                          end is earlier, which would turn an overnight 22:00-02:00 window
                          into 02:00-22:00. rc-picker only honours it with picker="time". */}
                      <DatePicker.RangePicker
                        picker="time"
                        order={false}
                        format="HH:mm"
                        size="large"
                        style={{ width: 300 }}
                        placeholder={["เวลาเริ่มต้น", "เวลาสิ้นสุด"]}
                        value={
                          schedule.scheduleStart || schedule.scheduleEnd
                            ? [schedule.scheduleStart ?? null, schedule.scheduleEnd ?? null]
                            : null
                        }
                        onChange={(range) =>
                          setSchedule((prev) => ({
                            ...prev,
                            scheduleStart: range?.[0] ?? undefined,
                            scheduleEnd: range?.[1] ?? undefined,
                          }))
                        }
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
              <div>
                <Radio value="custom">กำหนดวันเวลา - แบบละเอียด</Radio>
                {schedule.typeTime === "custom" && (
                  <div style={{ marginTop: 12, paddingLeft: 24 }}>
                    <Form.Item label="เวลาเริ่มต้น" style={{ marginBottom: 0 }}>
                      <DatePicker.RangePicker
                        showTime
                        format="DD/MM/YY HH:mm"
                        size="large"
                        style={{ width: 300 }}
                        value={
                          schedule.customStart && schedule.customEnd
                            ? [schedule.customStart, schedule.customEnd]
                            : undefined
                        }
                        onChange={(range) =>
                          setSchedule((prev) => ({
                            ...prev,
                            customStart: range?.[0] || undefined,
                            customEnd: range?.[1] || undefined,
                          }))
                        }
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            </Space>
          </Radio.Group>
        </Form.Item>

        <SectionBand />

        {item.scope === "PER_PRODUCT" ? (
          <Form.Item
            name="sites"
            label="แสดงที่เว็บไซต์"
            rules={[{ required: true, message: "กรุณาเลือกอย่างน้อยหนึ่งเว็บ", type: "array" }]}
          >
            <SitePicker />
          </Form.Item>
        ) : (
          // Tip/Rain always run on every site: the same section, all ticked and locked.
          // Not bound to the form, so the row's stored selection never gates its save.
          <Form.Item label="แสดงที่เว็บไซต์" required>
            <SitePicker value={WEBSITE_SYSTEM_PRODUCTS} disabled />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

// Edit modal for one website-system row: which sites it shows on (picked on a
// PER_PRODUCT row, shown locked on an ALL row) and when. It carries no text or
// channel: Core BO publishes the messages, so a save sends an empty message and
// no toast/notification (see buildUpdateWebsiteSystemRequest). Turning the row
// on/off is not part of this form - that is the table's own switch
// (useToggleWebsiteSystem). The actual form/state lives in EditModalBody,
// remounted per row via key={item.code} - see that component for why.
export const WebsiteSystemEditModal = NiceModal.create(({ item }: WebsiteSystemEditModalProps) => {
  const { visible, hide, remove } = useModal();
  return <EditModalBody key={item.code} item={item} visible={visible} hide={hide} remove={remove} />;
});
